import { MfaMethodEnum } from '@codezo/graphql';
import {
	Box,
	Group,
	PasswordInput,
	SegmentedControl,
	SimpleGrid,
	Stack,
	Text,
	TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconMail, IconMinus, IconShieldLock, IconX } from '@tabler/icons-react';
import { useEffect, useMemo, useRef } from 'react';

interface PersonalInfoStepProps {
	data: Record<string, unknown>;
	onDataChange: (_data: Record<string, unknown>) => void;
	onValidSubmit: (_data: Record<string, unknown>) => void;
	onPrevious: () => void;
	isFirstStep: boolean;
	isLastStep: boolean;
	triggerValidation: number;
}

// Password requirement checker
function checkPasswordRequirements(password: string) {
	return {
		length: password.length >= 8 && password.length <= 32,
		uppercase: /[A-Z]/.test(password),
		lowercase: /[a-z]/.test(password),
		number: /\d/.test(password),
		special: /[@$!%*?&#_-]/.test(password),
	};
}

export function PersonalInfoStep({
	data,
	onDataChange,
	onValidSubmit,
	triggerValidation,
}: PersonalInfoStepProps) {
	const lastTriggerRef = useRef(0);

	const form = useForm({
		initialValues: {
			firstName: (data.firstName as string) || '',
			lastName: (data.lastName as string) || '',
			jobPosition: (data.jobPosition as string) || '',
			email: (data.email as string) || '',
			password: (data.password as string) || '',
			confirmPassword: (data.confirmPassword as string) || '',
			mfaMethod: (data.mfaMethod as string) || MfaMethodEnum.Authenticator,
		},
		validate: {
			firstName: (value) => (value.trim() ? null : 'First name is required'),
			lastName: (value) => (value.trim() ? null : 'Last name is required'),
			jobPosition: (value) => (value.trim() ? null : 'Job position is required'),
			email: (value) => {
				if (!value.trim()) return 'Email is required';
				return /^\S+@\S+$/.test(value) ? null : 'Please enter a valid email';
			},
			password: (value) => {
				if (!value.trim()) return 'Password is required';
				const requirements = checkPasswordRequirements(value);

				// Check each requirement and provide specific feedback
				if (!requirements.length) return 'Password must be 8-32 characters';
				if (!requirements.uppercase)
					return 'Password must contain at least one uppercase letter';
				if (!requirements.lowercase)
					return 'Password must contain at least one lowercase letter';
				if (!requirements.number) return 'Password must contain at least one number';
				if (!requirements.special)
					return 'Password must contain at least one special character (@$!%*?&#_-)';

				return null;
			},
			confirmPassword: (value, values) => {
				if (!value.trim()) return 'Please confirm your password';
				return value === values.password ? null : 'Passwords do not match';
			},
			mfaMethod: (value) =>
				value ? null : 'Please select a two-factor authentication method',
		},
	});

	// Check password requirements in real-time
	const passwordRequirements = useMemo(
		() => checkPasswordRequirements(form.values.password),
		[form.values.password]
	);

	const handleSubmit = (values: typeof form.values) => {
		onValidSubmit(values);
	};

	// Update parent component whenever form values change
	useEffect(() => {
		// Pass the form values to parent for state management
		onDataChange(form.values);
	}, [form.values, onDataChange]);

	// Handle validation trigger from parent
	useEffect(() => {
		// Only validate if this is a new trigger (not the same as last time)
		if (triggerValidation > 0 && triggerValidation !== lastTriggerRef.current) {
			lastTriggerRef.current = triggerValidation;

			// Validate the form
			const validationResult = form.validate();

			// If validation passes (no errors), submit the form
			if (!validationResult.hasErrors) {
				onValidSubmit(form.values);
			}
		}
	}, [triggerValidation, form, onValidSubmit]);

	return (
		<Box>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
					<TextInput
						label="First Name"
						placeholder="Enter your first name"
						required
						{...form.getInputProps('firstName')}
						onChange={(event) => {
							form.setFieldValue('firstName', event.currentTarget.value);
						}}
					/>

					<TextInput
						label="Last Name"
						placeholder="Doe"
						required
						{...form.getInputProps('lastName')}
						onChange={(event) => {
							form.setFieldValue('lastName', event.currentTarget.value);
						}}
					/>

					<Box style={{ gridColumn: '1 / -1' }}>
						<TextInput
							label="Job Position"
							placeholder="e.g. Sustainability Manager, CEO, Operations Director"
							required
							{...form.getInputProps('jobPosition')}
							onChange={(event) => {
								form.setFieldValue('jobPosition', event.currentTarget.value);
							}}
						/>
					</Box>

					<Box style={{ gridColumn: '1 / -1' }}>
						<TextInput
							label="Email Address"
							placeholder="Enter your email address"
							required
							type="email"
							{...form.getInputProps('email')}
							onChange={(event) => {
								form.setFieldValue('email', event.currentTarget.value);
							}}
						/>
					</Box>

					<Box>
						<PasswordInput
							label="Password"
							placeholder="••••••••"
							required
							{...form.getInputProps('password')}
							onChange={(event) => {
								form.setFieldValue('password', event.currentTarget.value);
							}}
						/>
						<Stack gap={4} mt={8}>
							<Group gap={6}>
								{(() => {
									const pwd = form.values.password;
									if (pwd && passwordRequirements.length) {
										return (
											<IconCheck
												size={14}
												style={{ color: 'var(--mantine-color-teal-6)' }}
											/>
										);
									}
									if (pwd) {
										return (
											<IconX
												size={14}
												style={{ color: 'var(--mantine-color-red-6)' }}
											/>
										);
									}
									return (
										<IconMinus
											size={14}
											style={{ color: 'var(--mantine-color-gray-5)' }}
										/>
									);
								})()}
								<Text size="xs">8-32 characters long</Text>
							</Group>
							<Group gap={6}>
								{(() => {
									const pwd = form.values.password;
									if (pwd && passwordRequirements.uppercase) {
										return (
											<IconCheck
												size={14}
												style={{ color: 'var(--mantine-color-teal-6)' }}
											/>
										);
									}
									if (pwd) {
										return (
											<IconX
												size={14}
												style={{ color: 'var(--mantine-color-red-6)' }}
											/>
										);
									}
									return (
										<IconMinus
											size={14}
											style={{ color: 'var(--mantine-color-gray-5)' }}
										/>
									);
								})()}
								<Text size="xs">Contains uppercase letter</Text>
							</Group>
							<Group gap={6}>
								{(() => {
									const pwd = form.values.password;
									if (pwd && passwordRequirements.lowercase) {
										return (
											<IconCheck
												size={14}
												style={{ color: 'var(--mantine-color-teal-6)' }}
											/>
										);
									}
									if (pwd) {
										return (
											<IconX
												size={14}
												style={{ color: 'var(--mantine-color-red-6)' }}
											/>
										);
									}
									return (
										<IconMinus
											size={14}
											style={{ color: 'var(--mantine-color-gray-5)' }}
										/>
									);
								})()}
								<Text size="xs">Contains lowercase letter</Text>
							</Group>
							<Group gap={6}>
								{(() => {
									const pwd = form.values.password;
									if (pwd && passwordRequirements.number) {
										return (
											<IconCheck
												size={14}
												style={{ color: 'var(--mantine-color-teal-6)' }}
											/>
										);
									}
									if (pwd) {
										return (
											<IconX
												size={14}
												style={{ color: 'var(--mantine-color-red-6)' }}
											/>
										);
									}
									return (
										<IconMinus
											size={14}
											style={{ color: 'var(--mantine-color-gray-5)' }}
										/>
									);
								})()}
								<Text size="xs">Contains a number</Text>
							</Group>
							<Group gap={6}>
								{(() => {
									const pwd = form.values.password;
									if (pwd && passwordRequirements.special) {
										return (
											<IconCheck
												size={14}
												style={{ color: 'var(--mantine-color-teal-6)' }}
											/>
										);
									}
									if (pwd) {
										return (
											<IconX
												size={14}
												style={{ color: 'var(--mantine-color-red-6)' }}
											/>
										);
									}
									return (
										<IconMinus
											size={14}
											style={{ color: 'var(--mantine-color-gray-5)' }}
										/>
									);
								})()}
								<Text size="xs">Contains special character (@$!%*?&#_-)</Text>
							</Group>
						</Stack>
					</Box>

					<PasswordInput
						label="Confirm Password"
						placeholder="••••••••"
						required
						{...form.getInputProps('confirmPassword')}
						onChange={(event) => {
							form.setFieldValue('confirmPassword', event.currentTarget.value);
						}}
					/>
				</SimpleGrid>

				<Box>
					<Text size="sm" fw={500} mb="xs" mt="md">
						Two-Factor Authentication Method
					</Text>
					<Text size="xs" c="dimmed" mb="sm">
						Choose how you want to secure your account
					</Text>
					<SegmentedControl
						fullWidth
						value={form.values.mfaMethod}
						onChange={(value) => form.setFieldValue('mfaMethod', value)}
						data={[
							{
								label: (
									<Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
										<IconShieldLock size={18} />
										<Text size="sm">Authenticator App</Text>
									</Box>
								),
								value: MfaMethodEnum.Authenticator,
							},
							{
								label: (
									<Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
										<IconMail size={18} />
										<Text size="sm">Email</Text>
									</Box>
								),
								value: MfaMethodEnum.Email,
							},
						]}
					/>
					{form.values.mfaMethod === MfaMethodEnum.Authenticator && (
						<Text size="xs" c="dimmed" mt="xs">
							Use an authenticator app like Google Authenticator or Authy
						</Text>
					)}
					{form.values.mfaMethod === MfaMethodEnum.Email && (
						<Text size="xs" c="dimmed" mt="xs">
							We&apos;ll send a verification code to your email address
						</Text>
					)}
				</Box>
			</form>
		</Box>
	);
}
